import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";

const typeOptions = [
  { key: "all", label: "全部" },
  ...categories.map((item) => ({ key: item.key, label: item.label })),
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState<number | "all">("all");
  const [loading, setLoading] = useState(false);

  usePageMeta({
    title: keyword
      ? `搜索"${keyword}" - 棒棒TV影视搜索`
      : "影视搜索 - 棒棒TV 搜索你喜爱的影视作品",
    description:
      "在棒棒TV搜索电影、电视剧、综艺与纪录片，通过关键词快速匹配感兴趣的作品与资讯。",
    keywords: "棒棒TV搜索,影视搜索,电影搜索,电视剧搜索,影视内容查找",
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    if (!keyword) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(timer);
  }, [keyword]);

  const years = useMemo(
    () => Array.from(new Set(movies.map((movie) => movie.year))).sort((a, b) => b - a),
    []
  );

  const results = useMemo(() => {
    const trimmed = keyword.trim().toLowerCase();
    if (!trimmed) return [];
    return movies.filter((movie) => {
      const matchKeyword =
        movie.title.toLowerCase().includes(trimmed) ||
        movie.director.toLowerCase().includes(trimmed) ||
        movie.synopsis.toLowerCase().includes(trimmed) ||
        movie.genres.some((genre) => genre.toLowerCase().includes(trimmed)) ||
        movie.cast.some((member) => member.toLowerCase().includes(trimmed)) ||
        movie.tags.some((tag) => tag.toLowerCase().includes(trimmed));
      const matchType = typeFilter === "all" || movie.type === typeFilter;
      const matchYear = yearFilter === "all" || movie.year === yearFilter;
      return matchKeyword && matchType && matchYear;
    });
  }, [keyword, typeFilter, yearFilter]);

  const hotMovies = useMemo(
    () => [...movies].sort((a, b) => b.hotness - a.hotness).slice(0, 4),
    []
  );

  const submitSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("q", value);
    } else {
      next.delete("q");
    }
    setSearchParams(next);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 px-4 py-8 md:px-8 md:py-10">
        <div className="mx-auto max-w-[1180px]">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
            <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              影视搜索
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              输入作品名称、类型、演员或导演，快速找到你想看的影视内容。
            </p>

            <form
              className="mt-5 flex flex-col gap-3 sm:flex-row"
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch(input.trim());
              }}
            >
              <div className="relative flex-1">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
                <input
                  type="text"
                  value={input}
                  onChange={(event) => {
                    setInput(event.target.value);
                    submitSearch(event.target.value.trim());
                  }}
                  placeholder="例如：星际、悬疑、纪录片…"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-search-line"></i>
                搜索
              </button>
            </form>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
            <div className="min-w-0">
              {keyword && (
                <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
                  当前关键词：
                  <span className="rounded-md bg-accent-100 px-2.5 py-1 font-semibold text-accent-800">
                    {keyword}
                  </span>
                  <span className="text-foreground-400">
                    共 {loading ? "..." : results.length} 条结果
                  </span>
                </div>
              )}

              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 text-foreground-500">
                  <i className="ri-loader-4-line animate-spin text-3xl text-accent-500"></i>
                  <p className="mt-3 text-sm">正在搜索相关内容…</p>
                </div>
              ) : !keyword ? (
                <div className="rounded-lg border border-dashed border-background-300 py-16 text-center">
                  <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
                  <p className="mt-4 text-sm text-foreground-600">
                    输入关键词开始搜索吧，试试上面的热门推荐或直接浏览分类。
                  </p>
                </div>
              ) : results.length === 0 ? (
                <div className="rounded-lg border border-dashed border-background-300 py-16 text-center">
                  <i className="ri-emotion-happy-line text-4xl text-foreground-300"></i>
                  <h2 className="mt-4 text-base font-bold text-foreground-800">
                    没有找到与"{keyword}"相关的影视内容
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500">
                    换个关键词试试，或者看看下面这些热门推荐。
                  </p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                    <Link
                      to="/"
                      className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      返回首页
                    </Link>
                    <Link
                      to="/category/movie"
                      className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400"
                    >
                      浏览电影频道
                    </Link>
                  </div>
                </div>
              ) : (
                <div data-product-shop className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
                  {results.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              )}
            </div>

            <aside className="min-w-0 space-y-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="text-sm font-bold text-foreground-950">筛选结果</h2>
                <div className="mt-3">
                  <p className="mb-2 text-xs font-semibold text-foreground-500">按类型</p>
                  <div className="flex flex-wrap gap-2">
                    {typeOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setTypeFilter(option.key)}
                        className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                          typeFilter === option.key
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 text-foreground-600 hover:bg-background-200"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <p className="mb-2 text-xs font-semibold text-foreground-500">按年份</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setYearFilter("all")}
                      className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                        yearFilter === "all"
                          ? "bg-secondary-500 text-foreground-950"
                          : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      全部
                    </button>
                    {years.map((year) => (
                      <button
                        key={year}
                        type="button"
                        onClick={() => setYearFilter(year)}
                        className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                          yearFilter === year
                            ? "bg-secondary-500 text-foreground-950"
                            : "bg-background-100 text-foreground-600 hover:bg-background-200"
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="text-sm font-bold text-foreground-950">热门推荐</h2>
                <ul className="mt-3 space-y-2">
                  {hotMovies.map((movie) => (
                    <li key={movie.id}>
                      <Link
                        to={`/detail/${movie.id}`}
                        className="flex items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-background-100"
                      >
                        <span className="block h-14 w-10 shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img
                            src={movie.cover}
                            alt={`${movie.title} 封面 - 棒棒TV热门推荐`}
                            title={`${movie.title} - 棒棒TV`}
                            className="h-full w-full object-cover object-top"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground-800">
                            {movie.title}
                          </span>
                          <span className="block truncate text-xs text-foreground-500">
                            {movie.genres.join(" · ")}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}